import { useState } from "react";
import type { CSSProperties } from "react";
import { Card, Typography, Space, Button, Input, Divider, message } from "antd";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { authApi, errorMessage } from "../helpers/api";
import { GOOGLE_CLIENT_ID, MS_CLIENT_ID } from "../helpers/constants";
import { ensureMsalInitialized, msalInstance } from "../helpers/msal";
import type { TokenPairResponse } from "../api-models";

const DEV_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_DEV_LOGIN === "true";

// Card is 360 wide with antd's default 24px body padding.
const PROVIDER_BUTTON_WIDTH = 312;

// The Google button is rendered by Google's SDK and its look is fixed by their
// branding rules, so the Microsoft button is styled to match it instead.
const providerButtonStyle: CSSProperties = {
  width: PROVIDER_BUTTON_WIDTH,
  height: 40,
  borderRadius: 4,
  borderColor: "#dadce0",
  color: "#3c4043",
  fontSize: 14,
  fontWeight: 500,
};

function MicrosoftLogo() {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8.5" height="8.5" fill="#f25022" />
      <rect x="9.5" y="0" width="8.5" height="8.5" fill="#7fba00" />
      <rect x="0" y="9.5" width="8.5" height="8.5" fill="#00a4ef" />
      <rect x="9.5" y="9.5" width="8.5" height="8.5" fill="#ffb900" />
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [devEmail, setDevEmail] = useState("");

  async function completeLogin(path: string, data: Record<string, string>) {
    setLoading(true);
    try {
      const res = await authApi.post<TokenPairResponse>(path, data);
      login(res.data);
      navigate("/");
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleMicrosoftLogin() {
    if (!msalInstance) return;
    setLoading(true);
    try {
      await ensureMsalInitialized();
      const result = await msalInstance.loginPopup({ scopes: ["openid", "profile", "email"] });
      await completeLogin("/auth/microsoft", { idToken: result.idToken });
    } catch (err) {
      message.error(errorMessage(err));
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "calc(100vh - var(--glut-env-banner-height))", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f7fa" }}>
      <Card style={{ width: 360 }}>
        <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 0 }}>
          Glut
        </Typography.Title>
        <Typography.Text type="secondary" style={{ display: "block", textAlign: "center", marginBottom: 24 }}>
          Material Management System
        </Typography.Text>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {GOOGLE_CLIENT_ID && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GoogleLogin
                onSuccess={(cred) => cred.credential && completeLogin("/auth/google", { idToken: cred.credential })}
                onError={() => message.error("Google sign-in failed.")}
                width={PROVIDER_BUTTON_WIDTH}
                size="large"
                shape="rectangular"
                logo_alignment="center"
              />
            </div>
          )}
          {MS_CLIENT_ID && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                loading={loading}
                onClick={handleMicrosoftLogin}
                icon={<MicrosoftLogo />}
                style={providerButtonStyle}
              >
                Sign in with Microsoft
              </Button>
            </div>
          )}
          {!GOOGLE_CLIENT_ID && !MS_CLIENT_ID && !DEV_LOGIN_ENABLED && (
            <Typography.Text type="danger">
              No identity provider configured. Set VITE_GOOGLE_CLIENT_ID or VITE_MS_CLIENT_ID.
            </Typography.Text>
          )}
        </Space>

        {DEV_LOGIN_ENABLED && (
          <>
            <Divider plain>Dev sign-in (E2E tests only)</Divider>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="email@test.local"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                onPressEnter={() => devEmail && completeLogin("/auth/dev-login", { email: devEmail })}
              />
              <Button loading={loading} disabled={!devEmail} onClick={() => completeLogin("/auth/dev-login", { email: devEmail })}>
                Go
              </Button>
            </Space.Compact>
          </>
        )}
      </Card>
    </div>
  );
}
